import { useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";

const LINKS = [
    { label: "Companies", href: "#companies", testid: "nav-link-companies" },
    { label: "Services", href: "#services", testid: "nav-link-services" },
    { label: "Why Us", href: "#why-us", testid: "nav-link-why-us" },
    { label: "Contact", href: "#enquiry", testid: "nav-link-contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { scrollY } = useScroll();

    useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
                scrolled ? "border-white/10 bg-void/85 backdrop-blur-md" : "border-transparent bg-transparent"
            }`}
            data-testid="navbar"
        >
            <div className="flex items-center justify-between px-5 md:px-10 py-4">
                <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
                    <span className="block h-3.5 w-3.5 bg-blaze" />
                    <span className="font-display text-lg tracking-wide uppercase">Akash Network</span>
                </a>

                <nav className="hidden md:flex items-center gap-8">
                    {LINKS.map((link) => (
                        <a
                            key={link.href}
                            href={link.href}
                            data-testid={link.testid}
                            className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-blaze transition-colors duration-200"
                        >
                            {link.label}
                        </a>
                    ))}
                    <a
                        href="#enquiry"
                        data-testid="nav-quote-button"
                        className="group flex items-center gap-2 bg-blaze px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-white hover:bg-white hover:text-void transition-colors duration-200"
                    >
                        Get a Quote
                        <ArrowUpRight size={14} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                </nav>

                <button
                    className="md:hidden text-white"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                    data-testid="nav-menu-toggle"
                >
                    {open ? <X size={26} /> : <List size={26} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="md:hidden overflow-hidden border-t border-white/10 bg-void/95 backdrop-blur-md"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="flex flex-col px-5 py-6 gap-5">
                            {LINKS.map((link) => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    className="font-display text-2xl uppercase tracking-wide text-white/80 hover:text-blaze transition-colors duration-200"
                                >
                                    {link.label}
                                </a>
                            ))}
                            <a
                                href="#enquiry"
                                onClick={() => setOpen(false)}
                                className="mt-2 flex w-fit items-center gap-2 bg-blaze px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em]"
                                data-testid="nav-mobile-quote-button"
                            >
                                Get a Quote <ArrowUpRight size={14} weight="bold" />
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
